import { useState, useEffect } from "react";
import { fetchSession, type AuthenticatedUser } from "./api/auth";
import { getTenantConfig, DEFAULT_ANONYMOUS_TENANT_ID } from "./tenants/tenantConfig";

export default function AppRoot() {
  const [authStatus, setAuthStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(null);

  // Initial session check beim App-Start
  useEffect(() => {
    let mounted = true;

    const checkInitialSession = async () => {
      // NOTE: SSO token extraction now happens in main.tsx BEFORE React renders
      // This prevents race conditions where components call fetchSession() before tokens are stored

      // Check if we just completed SSO login (tokens in URL)
      const params = new URLSearchParams(window.location.search);
      const justCompletedSSO = params.get("auth") === "success";

      if (justCompletedSSO) {
        // Small delay to ensure tokens are readable from localStorage
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      try {
        const sessionData = await fetchSession();

        if (!mounted) return;

        if (sessionData?.authenticated && sessionData.user) {
          setCurrentUser(sessionData.user);
          setAuthStatus("authenticated");
          // Note: URL cleaning (provider/auth params) is now handled in main.tsx before React renders
        } else {
          setCurrentUser(null);
          setAuthStatus("unauthenticated");
        }
      } catch (error) {
        if (!mounted) return;
        console.error("[AppRoot] Session check failed:", error);
        setCurrentUser(null);
        setAuthStatus("unauthenticated");
      }
    };

    checkInitialSession();

    return () => {
      mounted = false;
    };
  }, []);

  // Loading State - Show spinner while checking authentication
  if (authStatus === "loading") {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "1rem"
      }}>
        <div style={{
          width: "24px",
          height: "24px",
          border: "3px solid #e5e7eb",
          borderTop: "3px solid #667eea",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }} />
        <p>Lade...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Determine which tenant to load
  let tenantId: string;
  let effectiveUser: AuthenticatedUser | undefined;

  if (authStatus === "authenticated" && currentUser?.tenantId) {
    // Authenticated user with tenant - use their tenant
    tenantId = currentUser.tenantId;
    effectiveUser = currentUser;
  } else {
    // Unauthenticated or user without tenant - use anonymous tenant
    tenantId = "anonymous";
    effectiveUser = undefined;
  }

  // Load tenant-specific UI
  const tenantConfig = getTenantConfig(tenantId);
  const TenantComponent = tenantConfig.component;

  return <TenantComponent currentUser={effectiveUser} />;
}
